/**
 * List pages для Block 2.0 + 2.1 (2026-05-26).
 * Editor — TECHDEBT (окрема міні-порція).
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  productionCalendar,
  vacationOrder,
  vacationCalculation,
  type ProductionCalendarDay,
  type VacationOrder,
  type VacationCalculation,
} from '@/api/vacation';
import { refs } from '@/api/refs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';


const fmt2 = (v: string | number | null) =>
  v == null ? '' : Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });


export function ProductionCalendarPage() {
  const year = new Date().getFullYear();
  const list = useQuery({
    queryKey: ['production-calendar', year],
    queryFn: () => productionCalendar.holidays({
      start_date: `${year}-01-01`,
      end_date: `${year}-12-31`,
      is_wartime: true,
    }),
  });
  const cols = useMemo<ColumnDef<ProductionCalendarDay>[]>(
    () => [
      { key: 'day_date', header: 'Дата', width: 130, sortValue: (r) => r.day_date ?? '', cell: (r) => r.day_date },
      {
        key: 'day_type', header: 'Тип', width: 150,
        sortValue: (r) => r.day_type ?? '',
        cell: (r) => {
          const v = r.day_type;
          if (v === 'HOLIDAY') return 'Свято';
          if (v === 'WEEKEND') return 'Вихідний';
          if (v === 'NON_WORKING_DAY') return 'Неробочий';
          return 'Робочий';
        },
      },
      { key: 'holiday_name', header: 'Назва', sortValue: (r) => r.holiday_name ?? '', cell: (r) => r.holiday_name },
      { key: 'seed_source', header: 'Джерело', width: 200, sortValue: (r) => r.seed_source ?? '', cell: (r) => r.seed_source },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Виробничий календар {year}</h2>
        <span style={{ marginLeft: 'auto', color: '#666' }}>
          Свят: {list.data?.length ?? 0} (воєнний стан)
        </span>
      </div>
      <DataTable<ProductionCalendarDay>
        rows={list.data ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="day_date"
      />
    </>
  );
}


export function VacationOrdersPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['vacation-orders'],
    queryFn: () => vacationOrder.list({ limit: 200 }),
  });
  const empQ = useQuery({ queryKey: ['employees-all'], queryFn: () => refs.employees() });
  const empName = useMemo(() => {
    const m = new Map<string, string>();
    empQ.data?.forEach((e) => m.set(e.id, e.name));
    return m;
  }, [empQ.data]);
  const cols = useMemo<ColumnDef<VacationOrder>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      { key: 'employee_id', header: 'Працівник', width: 220, sortValue: (r) => empName.get(r.employee_id) ?? r.employee_id, cell: (r) => empName.get(r.employee_id) ?? '—' },
      {
        key: 'vacation_type', header: 'Тип', width: 150,
        sortValue: (r) => r.vacation_type ?? '',
        cell: (r) => {
          const v = r.vacation_type;
          return {
            ANNUAL_BASIC: 'Основна', ANNUAL_ADDITIONAL: 'Додаткова',
            SOCIAL: 'Соціальна', UNPAID: 'Без ЗП', CREATIVE: 'Творча',
          }[v] ?? v;
        },
      },
      { key: 'vacation_start_date', header: 'З', width: 110, sortValue: (r) => r.vacation_start_date ?? '', cell: (r) => r.vacation_start_date },
      { key: 'vacation_end_date', header: 'По', width: 110, sortValue: (r) => r.vacation_end_date ?? '', cell: (r) => r.vacation_end_date },
      {
        key: 'days_count', header: 'Днів', width: 80, align: 'right',
        sortValue: (r) => r.days_count, cell: (r) => r.days_count,
      },
      {
        key: 'is_paid', header: 'Оплата', width: 90, align: 'center',
        sortValue: (r) => (r.is_paid ? 1 : 0), cell: (r) => (r.is_paid ? '✓' : '—'),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0), cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [empName],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Накази відпустки</h2>
        <button className="primary" onClick={() => navigate('/hr/vacation-orders/new')}>
          + Новий наказ
        </button>
      </div>
      <DataTable<VacationOrder>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        onRowDoubleClick={(r) => navigate(`/hr/vacation-orders/${r.id}`)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}


export function VacationCalculationsPage() {
  const list = useQuery({
    queryKey: ['vacation-calcs'],
    queryFn: () => vacationCalculation.list({ limit: 200 }),
  });
  const cols = useMemo<ColumnDef<VacationCalculation>[]>(
    () => [
      { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
      {
        key: 'document_date', header: 'Дата', width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      { key: 'employee_id', header: 'Працівник', width: 220, sortValue: (r) => r.employee_id ?? '', cell: (r) => r.employee_id },
      { key: 'vacation_start_date', header: 'Відпустка з', width: 130, sortValue: (r) => r.vacation_start_date ?? '', cell: (r) => r.vacation_start_date },
      {
        key: 'days_count', header: 'Днів', width: 80, align: 'right',
        sortValue: (r) => r.days_count, cell: (r) => r.days_count,
      },
      {
        key: 'vacation_gross_amount', header: 'Нараховано', width: 140, align: 'right',
        sortValue: (r) => Number(r.vacation_gross_amount), cell: (r) => fmt2(r.vacation_gross_amount),
      },
      {
        key: 'net_amount', header: 'До виплати', width: 140, align: 'right',
        sortValue: (r) => Number(r.net_amount), cell: (r) => fmt2(r.net_amount),
      },
      {
        key: 'posted', header: 'Проведено', width: 120, align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0), cell: (r) => (r.posted ? '✓ так' : 'ні'),
      },
    ],
    [],
  );
  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Розрахунки відпускних</h2>
      </div>
      <DataTable<VacationCalculation>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
